import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, of, forkJoin, map, catchError, tap, from, switchMap } from 'rxjs';
import { FirestoreService } from './firestore.service';
import { CollectionTables } from '../models/CollectionTables.model';
import {
  formatFirestoreDateLong,
  FirestoreTimestampField,
} from '../utils/firestore-timestamp.util';
import { Request } from '../models/request.model';
import { Storage, ref, listAll, deleteObject } from '@angular/fire/storage';
import { Firestore, collection, getDocs, deleteDoc, doc } from '@angular/fire/firestore';
import { Auth } from '@angular/fire/auth';

export interface RequestDetailsData {
  request: Request | null;
  invoices: any[];
  attachments: any[];
  loading: boolean;
  error: string | null;
}

export interface QuoteModel {
  subtotal: string;
  tax: string;
  total: string;
  url?: string;
  date?: string;
  items: QuoteItem[];
  currencyCode: string;
  taxPercentage: number;
  taxAbbreviation: string;
}

export interface QuoteItem {
  service: string;
  price: string | number;
  currency?: string;
}

export interface InvoiceModel {
  id: string;
  number?: string;
  subtotal?: string;
  total?: string;
  currency?: string;
  commission?: string;
  country?: string;
  paid?: boolean | null;
  paymentStarted?: number;
  paymentCompleted?: number;
  paymentIntent?: string;
  clientSecret?: string;
  invoice?: string; // PDF URL
  date?: FirestoreTimestampField;
}

export interface AttachmentModel {
  id: string;
  fileName: string;
  downloadUrl: string;
  mimeType: string;
  createdOn: FirestoreTimestampField;
  createdBy: string;
}

@Injectable({
  providedIn: 'root'
})
export class RequestDetailsService {
  private firestoreService = inject(FirestoreService);
  private storage = inject(Storage);
  private firestore = inject(Firestore);
  private auth = inject(Auth);
  
  private requestSubject = new BehaviorSubject<Request | null>(null);
  private invoicesSubject = new BehaviorSubject<any[]>([]);
  private attachmentsSubject = new BehaviorSubject<any[]>([]);
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private errorSubject = new BehaviorSubject<string | null>(null);

  // Public observables
  request$ = this.requestSubject.asObservable();
  invoices$ = this.invoicesSubject.asObservable();
  attachments$ = this.attachmentsSubject.asObservable();
  loading$ = this.loadingSubject.asObservable();
  error$ = this.errorSubject.asObservable();

  // Combined data observable
  data$ = new BehaviorSubject<RequestDetailsData>({
    request: this.requestSubject.value,
    invoices: this.invoicesSubject.value,
    attachments: this.attachmentsSubject.value,
    loading: this.loadingSubject.value,
    error: this.errorSubject.value
  });

  /**
   * Load complete request details including request, invoices, and attachments
   */
  loadRequestDetails(requestId: string): Observable<RequestDetailsData> {
    console.log('RequestDetailsService: Loading request details for ID:', requestId);
    this.loadingSubject.next(true);
    this.errorSubject.next(null);

    return forkJoin({
      request: this.getRequestById(requestId),
      invoices: this.getInvoices(requestId),
      attachments: this.getAttachments(requestId)
    }).pipe(
      map(({ request, invoices, attachments }) => {
        this.requestSubject.next(request);
        this.invoicesSubject.next(invoices);
        this.attachmentsSubject.next(attachments);
        this.loadingSubject.next(false);
        
        return {
          request,
          invoices,
          attachments,
          loading: false,
          error: null
        };
      }),
      catchError((error) => {
        console.error('Error loading request details:', error);
        this.errorSubject.next(error.message || 'Failed to load request details');
        this.loadingSubject.next(false);
        return of({
          request: null,
          invoices: [],
          attachments: [],
          loading: false,
          error: error.message || 'Failed to load request details'
        });
      })
    );
  }

  /**
   * Get request by ID
   */
  getRequestById(requestId: string): Observable<Request | null> {
    console.log('RequestDetailsService: Getting request by ID:', requestId);
    return this.firestoreService.getDocument(CollectionTables.requests, requestId).pipe(
      map((doc: any) => {
        console.log('RequestDetailsService: Document received:', doc);
        if (doc) {
          const transformed = this.transformFirebaseRequest(doc);
          console.log('RequestDetailsService: Transformed request:', transformed);
          return transformed;
        }
        return null;
      }),
      catchError((error) => {
        console.error('RequestDetailsService: Error fetching request:', error);
        return of(null);
      })
    );
  }

  /**
   * Get invoices for a request (matches Flutter implementation)
   */
  getInvoices(requestId: string): Observable<InvoiceModel[]> {
    console.log('Fetching invoices for request:', requestId);
    return this.firestoreService.getCollection(
      `${CollectionTables.requests}/${requestId}/${CollectionTables.invoices}`
    ).pipe(
      map((docs: any[]) => {
        console.log('Raw invoice docs:', docs);
        const invoices = docs
          .sort((a, b) => (b.date || 0) - (a.date || 0)) // Sort by date descending
          .map(doc => {
            console.log('Processing invoice doc:', doc);
            return {
              id: doc.id,
              number: doc.number,
              subtotal: doc.subtotal,
              total: doc.total,
              currency: doc.currency,
              commission: doc.commission,
              country: doc.country,
              paid: doc.paid,
              paymentStarted: doc.paymentStarted,
              paymentCompleted: doc.paymentCompleted,
              paymentIntent: doc.paymentIntent,
              clientSecret: doc.clientSecret,
              invoice: doc.invoice, // PDF URL
              date: doc.date
            };
          });
        console.log('Mapped invoices:', invoices);
        return invoices;
      }),
      catchError((error) => {
        console.error('Error fetching invoices:', error);
        return of([]);
      })
    );
  }

  /**
   * Get attachments for a request
   */
  getAttachments(requestId: string): Observable<AttachmentModel[]> {
    return this.firestoreService.getCollection(
      `${CollectionTables.requests}/${requestId}/${CollectionTables.attachments}`
    ).pipe(
      map((docs: any[]) => 
        docs.map(doc => ({
          id: doc.id,
          fileName: doc.fileName || '',
          downloadUrl: doc.downloadUrl || '',
          mimeType: doc.mimeType || '',
          createdOn: doc.createdOn,
          createdBy: doc.createdBy || ''
        }))
      ),
      catchError((error) => {
        console.error('Error fetching attachments:', error);
        return of([]);
      })
    );
  }

  /**
   * Parse quote JSON string — matches Flutter QuoteModel.fromJson.
   */
  parseQuote(quoteJson: string): QuoteModel | null {
    try {
      const quote = JSON.parse(quoteJson);
      const rawItems = Array.isArray(quote.items) ? quote.items : [];
      const items: QuoteItem[] = rawItems.map((item: unknown) => {
        if (item && typeof item === 'object' && !Array.isArray(item)) {
          const map = item as Record<string, unknown>;
          // Prefer named keys; fall back to insertion-order values (Flutter e.values.toList()).
          const values = Object.values(map);
          return {
            service: String(map['service'] ?? values[0] ?? ''),
            price: (map['price'] ?? values[1] ?? '0') as string | number,
            ...(map['currency'] != null ? { currency: String(map['currency']) } : {}),
          };
        }
        return { service: '', price: '0' };
      });

      return {
        subtotal: String(quote.subtotal ?? '0'),
        tax: String(quote.tax ?? '0'),
        total: String(quote.total ?? '0'),
        url: quote.url ? String(quote.url) : undefined,
        date: quote.date != null ? String(quote.date) : undefined,
        items,
        currencyCode: String(quote.currencyCode ?? quote.currency ?? ''),
        taxPercentage: Number(quote.taxPercentage ?? 0),
        taxAbbreviation: String(quote.taxAbbreviation ?? 'Tax'),
      };
    } catch (error) {
      console.error('Error parsing quote:', error);
      return null;
    }
  }

  /**
   * Update request status (without automatic timestamps - matches Flutter)
   */
  updateRequestStatus(requestId: string, status: string): Observable<boolean> {
    return this.firestoreService.updateDocumentRaw(
      CollectionTables.requests,
      requestId,
      { status } as any
    ).pipe(
      tap(() => {
        // Refresh request data after update
        this.getRequestById(requestId).subscribe(request => {
          this.requestSubject.next(request);
        });
      }),
      map(() => true),
      catchError((error) => {
        console.error('Error updating request status:', error);
        return of(false);
      })
    );
  }

  /**
   * Update quote acceptance (without automatic timestamps - matches Flutter)
   */
  acceptQuote(requestId: string): Observable<boolean> {
    return this.firestoreService.updateDocumentRaw(
      CollectionTables.requests,
      requestId,
      { 
        status: 'accepted',
        quoteAccepted: true 
      } as any
    ).pipe(
      tap(() => {
        // Refresh request data after update
        this.getRequestById(requestId).subscribe(request => {
          this.requestSubject.next(request);
        });
      }),
      map(() => true),
      catchError((error) => {
        console.error('Error accepting quote:', error);
        return of(false);
      })
    );
  }

  /**
   * Update request vendor (businessId and businessName)
   */
  updateRequestVendor(requestId: string, businessId: string, businessName: string): Observable<boolean> {
    return this.firestoreService.updateDocumentRaw(
      CollectionTables.requests,
      requestId,
      {
        providerBusinessId: businessId,
        providerBusinessName: businessName
      } as any
    ).pipe(
      tap(() => {
        // Refresh request data after update
        this.getRequestById(requestId).subscribe(request => {
          this.requestSubject.next(request);
        });
      }),
      map(() => true),
      catchError((error) => {
        console.error('Error updating request vendor:', error);
        return of(false);
      })
    );
  }

  /**
   * Cancel request
   */
  cancelRequest(requestId: string, currentStatus: string): Observable<boolean> {
    let newStatus = 'cancelled';
    
    // Different cancellation logic based on current status
    if (currentStatus === 'new') {
      newStatus = 'cancelled';
    } else if (currentStatus === 'estimated' || currentStatus === 'assigned') {
      newStatus = 'pending';
    }
    
    return this.updateRequestStatus(requestId, newStatus);
  }

  /**
   * Delete request and all related data (matches Flutter implementation)
   * 1. Deletes all files from Firebase Storage
   * 2. Deletes all attachments subcollection documents
   * 3. Deletes the main request document
   */
  deleteRequest(requestId: string): Observable<boolean> {
    const currentUser = this.auth.currentUser;
    
    if (!currentUser) {
      console.error('Cannot delete request: User not authenticated');
      return of(false);
    }

    const userId = currentUser.uid;
    
    return new Observable<boolean>((observer) => {
      // Step 1: Delete all files from Firebase Storage
      const storageRef = ref(this.storage, `attachments/uid_${userId}/rid_${requestId}`);
      
      from(listAll(storageRef))
        .pipe(
          switchMap((listResult) => {
            // Delete all files in parallel
            const deletePromises = listResult.items.map((itemRef) => 
              deleteObject(itemRef).catch(err => {
                console.warn('Error deleting file:', itemRef.fullPath, err);
              })
            );
            return from(Promise.all(deletePromises));
          }),
          switchMap(() => {
            // Step 2: Delete all attachments subcollection documents
            const attachmentsRef = collection(
              this.firestore,
              `${CollectionTables.requests}/${requestId}/${CollectionTables.attachments}`
            );
            return from(getDocs(attachmentsRef));
          }),
          switchMap((attachmentsSnapshot) => {
            // Delete all attachment documents in parallel
            const deletePromises = attachmentsSnapshot.docs.map((attachmentDoc) =>
              deleteDoc(attachmentDoc.ref).catch(err => {
                console.warn('Error deleting attachment:', attachmentDoc.id, err);
              })
            );
            return from(Promise.all(deletePromises));
          }),
          switchMap(() => {
            // Step 3: Delete the main request document
            const requestRef = doc(this.firestore, CollectionTables.requests, requestId);
            return from(deleteDoc(requestRef));
          })
        )
        .subscribe({
          next: () => {
            console.log('Request and all related data deleted successfully');
            observer.next(true);
            observer.complete();
          },
          error: (error) => {
            console.error('Error deleting request:', error);
            observer.next(false);
            observer.complete();
          }
        });
    });
  }

  /**
   * Update request description
   */
  updateDescription(requestId: string, description: string): Observable<boolean> {
    return this.firestoreService.updateDocument(
      CollectionTables.requests,
      requestId,
      { description } as any
    ).pipe(
      tap(() => {
        // Refresh request data after update
        this.getRequestById(requestId).subscribe(request => {
          this.requestSubject.next(request);
        });
      }),
      map(() => true),
      catchError((error) => {
        console.error('Error updating description:', error);
        return of(false);
      })
    );
  }

  /**
   * Transform Firebase request data to Angular Request model
   */
  private transformFirebaseRequest(doc: any): Request {
    // Provider business id; null when none so callers can detect "no vendor".
    const providerBusinessId = doc.providerBusinessId || '';
    const providerBusinessName = doc.providerBusinessName || '';
    return {
      id: doc.id || doc.requestId || '',
      service: doc.service || '',
      description: doc.description || '',
      status: doc.status || 'new',
      clientId: doc.clientId || '',
      clientName: doc.clientName || '',
      providerId: doc.providerId || '',
      providerBusinessId: providerBusinessId || null as unknown as string,
      providerBusinessName: providerBusinessName,
      managementId: doc.managementId || '',
      managementName: doc.managementName || '',
      managementBusinessId: doc.managementBusinessId || '',
      managementBusinessName: doc.managementBusinessName || '',
      createdOn: doc.createdOn,
      agentId: doc.agentId || '',
      agentName: doc.agentName || '',
      type: doc.type || '',
      subtype: doc.subtype || '',
      propertyId: doc.propertyId || '',
      unitId: doc.unitId || '',
      unitNumber: doc.unitNumber || '',
      address: doc.address || null,
      availableDate: doc.availableDate || null,
      slotId: doc.slotId || null,
      quote: doc.quote || null,
      quoteAccepted: doc.quoteAccepted || false,
      includeTax: doc.includeTax || false,
      reviewId: doc.reviewId || null,
      createdBy: doc.createdBy || '',
      schedule: doc.schedule || null,
      scheduledDate: doc.scheduledDate || null,
      providerLogo: doc.providerLogo || '',
      providerName: providerBusinessName || doc.providerName || '',
      requestNumber: doc.requestNumber || `#${doc.id?.slice(-6) || '000000'}`,
      dateCreated:
        doc.dateCreated ||
        (doc.createdOn ? formatFirestoreDateLong(doc.createdOn) : ''),
      agent: doc.agent || doc.agentName || '',
      unreadMessages: doc.unreadMessages || 0
    };
  }

  /**
   * Get stepper steps based on request status and data
   */
  getStepperSteps(request: Request): string[] {
    const steps = ['stepper_info', 'stepper_schedule', 'stepper_details'];

    if (request.status !== 'new' && request.quote) {
      if (request.status === 'estimated') {
        steps.push('stepper_quote');
      }
    }

    if (request.status !== 'new' && (!request.quote || request.quote === '')) {
      if (request.slotId && request.status !== 'cancelled') {
        steps.push('stepper_assign');
      }
      
      if (request.status && ['assigned', 'started', 'completed', 'confirmed', 'finished', 'dispatched', 'invoiced'].includes(request.status)) {
        steps.push('stepper_service');
      }
    }

    if (request.quoteAccepted) {
      if (request.status !== 'cancelled') {
        steps.push('stepper_quote');
      }
      
      if (request.slotId && request.status !== 'cancelled') {
        steps.push('stepper_assign');
      }
      
      if (request.status && ['assigned', 'started', 'completed', 'confirmed', 'finished', 'dispatched', 'invoiced'].includes(request.status)) {
        steps.push('stepper_service');
      }
    }

    return steps;
  }

  /**
   * Get step icons based on request status and data
   */
  getStepIcons(request: Request): string[] {
    const icons = ['location_history', 'schedule', 'details'];

    // Add quote icon if needed
    if (request.status !== 'new' && 
        request.status !== 'cancelled' && 
        request.quote) {
      icons.push('monetization_on_sharp');
    }

    // Add assignment icon if needed
    if ((request.quoteAccepted && request.slotId && request.status !== 'cancelled') ||
        (!request.quote || request.quote === '')) {
      icons.push('assignment_outlined');
    }

    // Add service icon if needed
    if (request.status && ['assigned', 'started', 'completed', 'confirmed', 'finished', 'dispatched', 'invoiced'].includes(request.status)) {
      icons.push('directions_walk');
    }

    return icons;
  }
}
